import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'
import { DateField } from '@/ui/form/controls/DateField'
import { DateAndTimeField } from '@/ui/form/controls/DateAndTimeField'

/**
 * Pole daty panelu admina oparte o `DateField`/`DateAndTimeField` z designu aplikacji.
 *
 * Zastępuje zestaw `DayPickerInput` (react-day-picker 7) + `CustomInputMask` + osobna
 * ikonka kalendarza, którym panel obsługiwał daty. Na zewnątrz operuje `Date`/`Moment`,
 * bo tak trzymają to ekrany panelu; w środku tłumaczy na format `YYYY-MM-DD [HH:mm]`,
 * którego oczekują pola z `@/ui`.
 */
type AdminDateFieldProps = {
  value?: Date | Moment | string | null
  onChange: (date: Date | null) => void
  /** Dodaje wybór godziny (odpowiednik dawnego `withTime`). */
  withTime?: boolean
  disabled?: boolean
  placeholder?: string
  className?: string
  classNameWrapper?: string
  openDirection?: 'down' | 'up'
}

const toFieldValue = (
  value: AdminDateFieldProps['value'],
  withTime: boolean,
): string | null => {
  if (value == null || value === '') return null
  const m = moment(value as any)
  if (!m.isValid()) return null
  return m.format(withTime ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD')
}

export const AdminDateField = ({
  value,
  onChange,
  withTime = false,
  disabled,
  placeholder,
  className,
  classNameWrapper,
  openDirection = 'down',
}: AdminDateFieldProps) => {
  const fieldValue = toFieldValue(value, withTime)

  const handle = (next: string | null) => {
    if (!next) {
      onChange(null)
      return
    }
    const m = moment(next, withTime ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD')
    onChange(m.isValid() ? m.toDate() : null)
  }

  const shared = {
    value: fieldValue,
    setValue: handle,
    disabled,
    placeholder,
    className,
    openDirection,
  }

  // `inputSize` i `classNameWrapper` ma tylko `DateField`; `DateAndTimeField` rozsypuje
  // resztę propsów na <input> i React krzyczy o nieznanym atrybucie.
  return withTime ? (
    <DateAndTimeField {...shared} />
  ) : (
    <DateField {...shared} classNameWrapper={classNameWrapper} inputSize="small" />
  )
}
